/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useMemo, useRef } from 'react';
import ArithmeticEditor from './ArithmeticEditor';
import { useStrategySchemaV2 } from '../../../hooks/useStrategySchemaV2';
import { useStrategyValidation } from '../../../hooks/useStrategyValidation';

export default function NodeInspector({ node, onChange, allNodes = [], allComparisons = [], allSections = {} }) {
  const { normalized } = useStrategySchemaV2() || {};
  const validation = useStrategyValidation(allNodes, allComparisons, allSections);
  const formulaTextareaRef = useRef(null);

  const descriptor = useMemo(() => {
    if (!node) return null;
    if (node.kind === 'expression') {
      return { name: 'Expression', params: [{ name: 'formula', type: 'string', required: true, default: '' }], outputs:['value'] };
    }
    if (!normalized) return null;
    const pool = [...(normalized.indicators||[]), ...(normalized.operators||[]), ...(normalized.actions||[])];
    return pool.find(d => d.name === node.name) || null;
  }, [node, normalized]);

  if (!node) return <div className="text-xs text-textSecondary">No node selected.</div>;
  if (node.type !== 'arithmetic' && !descriptor) return <div className="text-xs text-textSecondary">Descriptor not found.</div>;

  const updateParam = (pName, value) => {
    onChange({ ...node, params: { ...node.params, [pName]: value } });
  };

  const insertAtCursor = (text) => {
    const el = formulaTextareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const current = el.value;
    const next = current.slice(0,start) + text + current.slice(end);
    // find the formula param name reliably (we know descriptor for expression has 'formula')
    const formulaKey = 'formula';
    onChange({ ...node, params: { ...node.params, [formulaKey]: next } });
    requestAnimationFrame(()=>{
      el.focus();
      const caret = start + text.length;
      el.selectionStart = el.selectionEnd = caret;
    });
  };

  const referenceHelper = node.kind === 'expression' ? (
    <ReferenceHelper allNodes={allNodes} currentId={node.id} insert={insertAtCursor} />
  ) : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold tracking-wide">Node: {node.name}</h4>
        <span className="text-[10px] text-textSecondary">{node.id}</span>
      </div>
      {Array.isArray(node.outputs) && node.outputs.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-textSecondary">Outputs</div>
          <div className="flex flex-wrap gap-1">
            {node.outputs.map(o => {
              const active = node.defaultOutput === o;
              return (
                <button
                  key={o}
                  type="button"
                  onClick={()=>onChange({ ...node, defaultOutput: o })}
                  className={`px-2 py-0.5 rounded text-[10px] border ${active? 'bg-primary/30 border-primary' : 'bg-muted/40 border-borderDefault hover:bg-muted/60'}`}
                >{o}</button>
              );
            })}
          </div>
        </div>
      )}
      {node.type === 'arithmetic' && (
        <ArithmeticEditor
          node={node}
          onChange={(updated)=>onChange(updated)}
          availableSeries={allNodes.filter(n=>n.id!==node.id && n.type!=='comparison').flatMap(n => {
            if (Array.isArray(n.outputs) && n.outputs.length > 0) {
              if (n.outputs.length === 1) return [{ value:n.id, label:n.id }];
              return n.outputs.map(o => ({ value:`${n.id}:${o}`, label:`${n.id}.${o}` }));
            }
            return [{ value:n.id, label:n.id }];
          })}
        />
      )}
      {node.type !== 'arithmetic' && descriptor.params.length === 0 && (
        <div className="text-[11px] text-textSecondary">No parameters.</div>
      )}
      {node.type !== 'arithmetic' && (
        <form className="space-y-2">
        {descriptor.params.map(p => (
          <div key={p.name} className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-textSecondary">
              {p.name}{p.required ? '*' : ''}
            </label>
            {p.enum ? (
              <select
                className="h-8 text-xs rounded-md border bg-background"
                value={node.params[p.name] ?? ''}
                onChange={(e)=>updateParam(p.name, e.target.value)}
              >
                <option value="" disabled>Select</option>
                {p.enum.map(v => <option key={v} value={v}>{String(v)}</option>)}
              </select>
            ) : p.type === 'number' || p.type === 'integer' ? (
              <input
                type="number"
                className="h-8 text-xs rounded-md border bg-background"
                value={node.params[p.name] ?? ''}
                onChange={(e)=>updateParam(p.name, e.target.value === '' ? '' : (p.type==='integer'? parseInt(e.target.value,10): parseFloat(e.target.value)))}
                min={p.min ?? undefined}
                max={p.max ?? undefined}
                step={p.type==='number'? 0.01 : 1}
              />
            ) : p.type === 'boolean' ? (
              <select
                className="h-8 text-xs rounded-md border bg-background"
                value={String(node.params[p.name] ?? p.default ?? false)}
                onChange={(e)=>updateParam(p.name, e.target.value === 'true')}
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : node.kind === 'expression' && p.name === 'formula' ? (
              <>
                <textarea
                  ref={formulaTextareaRef}
                  className="min-h-[80px] text-xs rounded-md border bg-background p-2 font-mono"
                  value={node.params[p.name] ?? ''}
                  onChange={(e)=>updateParam(p.name, e.target.value)}
                  placeholder="e.g. (n1.close - n2.open) / n2.open"
                />
                {referenceHelper}
                <FormulaErrors nodeId={node.id} validation={validation} formula={node.params[p.name] ?? ''} />
              </>
            ) : (
              <input
                type="text"
                className="h-8 text-xs rounded-md border bg-background"
                value={node.params[p.name] ?? ''}
                onChange={(e)=>updateParam(p.name, e.target.value)}
                placeholder={p.default !== undefined ? String(p.default) : ''}
              />
            )}
          </div>
        ))}
        </form>
      )}
    </div>
  );
}

function FormulaErrors({ nodeId, validation }) {
  if (!validation) return null;
  const errs = validation.errors.filter(e => e.type === 'node' && e.id === nodeId && (e.code.startsWith('FORMULA_') || e.code === 'FORMULA_EMPTY'));
  if (errs.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {errs.map((e,i)=>(
        <li key={i} className="text-[10px] text-red-600 flex items-start gap-1">
          <span className="mt-0.5">•</span>
          <span>{e.msg}</span>
        </li>
      ))}
    </ul>
  );
}

function ReferenceHelper({ allNodes, currentId, insert }) {
  const others = allNodes.filter(n => n.id !== currentId);
  if (others.length === 0) return null;
  return (
    <div className="mt-1 rounded border bg-muted/30 p-1">
      <div className="text-[9px] uppercase tracking-wide text-textSecondary mb-1">Insert reference</div>
      <div className="flex flex-wrap gap-1 max-h-20 overflow-auto">
        {others.map(n => {
          const multi = Array.isArray(n.outputs) && n.outputs.length > 1;
          const baseLabel = multi ? `${n.id}(*${n.defaultOutput||''})` : n.id;
          return (
            <div key={n.id} className="flex items-center gap-1">
              <button
                type="button"
                className="px-1.5 py-0.5 rounded bg-background border text-[10px] hover:bg-muted"
                onClick={()=>insert(n.id)}
                title={multi ? `Inserts default output (${n.defaultOutput})` : 'Insert node ref'}
              >{baseLabel}</button>
              {Array.isArray(n.outputs) && n.outputs.length > 0 && (
                <div className="flex gap-0.5">
                  {n.outputs.map(o => (
                    <button
                      key={o}
                      type="button"
                      className="px-1 py-0.5 rounded bg-muted/40 text-[9px] hover:bg-muted border"
                      onClick={()=>insert(`${n.id}.${o}`)}
                      title={`Insert ${n.id}.${o}`}
                    >.{o}</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
